<template>
    <ElevatedCard :widthType="'twoThirds'">
        <HeaderWidget
            :iconUrl="loadIcon"
            :mode-edition="true"
            :title="widget.libelle"
            @dupliquer="dupliquer"
            @filtre="filtre"
            @suppression="suppression"
        />
        <div>
            <span v-if="widget.sites == null || widget.sites.length > 1" class="site-widget">{{
                $t('multisites')
            }}</span>
            <span v-for="site in widget.sites" v-else :key="site.id" class="site-widget">
                {{ site.description }}
                <span v-if="site.id !== widget.sites[widget.sites.length - 1].id">, </span>
            </span>
        </div>
        <div class="graphs-container">
            <PieChart
                :colors="[getColorEnCours(), getColorARealiser(), getColorRealiser()]"
                :height="'90%'"
                :isLoading="false"
                :labels="[$t('widgetPreventive.inProgress'), $t('widgetPreventive.toDo'), $t('widgetPreventive.done')]"
                :seriesValues="donutSeries"
                :showDataLabels="true"
                :showLegend="true"
                :title="titlePie"
                :width="'350px'"
                class="pie-chart-container chart-width"
                position="right"
                @dataPointClicked="naviguerVersRecherchePie"
            />
            <BarGraph
                :categories="getMonths()"
                :colors="[getColorEnCours(), getColorARealiser(), getColorRealiser()]"
                :customTooltip="customTooltip"
                :loading="false"
                :seriesData="barSeries"
                :showLegend="false"
                class="bar-graph-container chart-width2"
                @dataPointClicked="naviguerVersRechercheBar"
            />
        </div>
    </ElevatedCard>
</template>

<script lang="ts">
import dayjs from 'dayjs';
import localeFr from 'dayjs/locale/fr';
import ElevatedCard from '../../../components/ui-components/ElevatedCard.vue';
import HeaderWidget from '../../../components/ui-components/HeaderWidget.vue';
import { defineComponent, PropType } from 'vue';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import { Widget } from '@/models/Widget';
import PieChart from '@/components/ui-components/PieChart.vue';
import BarGraph from '@/components/ui-components/BarGraph.vue';

dayjs.locale(localeFr);

export default defineComponent({
    name: 'EditionPreventive',
    components: { BarGraph, PieChart, ElevatedCard, HeaderWidget },
    props: {
        widget: { type: Object as PropType<Widget>, required: true },
    },
    data() {
        const today = dayjs();
        const dateDebut = dayjs()
            .set('month', today.get('month') - 5)
            .startOf('month');

        const dateFin = dayjs()
            .set('month', today.get('month') + 6)
            .endOf('month');
        const dateFinMois = dayjs().endOf('month');
        const dateDebutCircleData = dateFinMois.set('month', dateFinMois.get('month') - 11).startOf('month');
        return {
            dateDebut,
            dateFin,
            dateFinMois,
            dateDebutCircleData,
            barSeries: [
                {
                    name: 'En cours',
                    data: [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
                },
                {
                    name: 'A réaliser',
                    data: [3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3],
                },
                {
                    name: 'Réalisées',
                    data: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
                },
            ],
            donutSeries: [10, 20, 30],
        };
    },
    methods: {
        getColorEnCours() {
            return getComputedStyle(document.documentElement).getPropertyValue('--edition-clair');
        },
        getColorARealiser() {
            return getComputedStyle(document.documentElement).getPropertyValue('--edition-medium');
        },
        getColorRealiser() {
            return getComputedStyle(document.documentElement).getPropertyValue('--edition-fonce');
        },
        getMonths(): string[] {
            const start = dayjs().subtract(5, 'month');
            const months = [];
            const locale = this.$store.getters.getLangueSelectionnee?.locale || 'fr';
            for (let i = 0; i < 12; i++) {
                months.push(start.locale(locale).clone().add(i, 'month').format('MMM'));
            }
            return months;
        },
        getEtatFromIndex(index: number): EtatsTravaux | undefined {
            switch (index) {
                case 0:
                    return EtatsTravaux.enCours;
                case 1:
                    return EtatsTravaux.aRealiser;
                case 2:
                    return EtatsTravaux.realise;
                default:
                    break;
            }
        },
        filtre() {
            this.$emit('filtre');
        },
        dupliquer() {
            this.$emit('dupliquer');
        },
        suppression() {
            this.$emit('suppression');
        },
    },
    computed: {
        loadIcon() {
            return require('@/assets/img/shield.png');
        },
        titlePie(): string {
            const locale = this.$store.getters.getLangueSelectionnee?.locale || 'fr';
            return (
                this.$t('widgetPreventive.from') +
                ' ' +
                this.dateDebutCircleData.locale(locale).format('MMMM YYYY') +
                ' ' +
                this.$t('widgetPreventive.to') +
                ' ' +
                this.dateFinMois.locale(locale).format('MMMM YYYY')
            );
        },
        customTooltip() {
            return ({ series, dataPointIndex }: any) => {
                let currentTotal = 0;
                series.forEach((s: any) => {
                    currentTotal += s[dataPointIndex];
                });
                return `<div class="custom-tooltip">
                        <span><b>Total: </b>
                            ${currentTotal}
                        </span>
                    </div>`;
            };
        },
    },
});
</script>

<style scoped>
.graphs-container {
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    height: 75%;
}
@media screen and (max-height: 800px) {
    .graphs-container {
        height: 65%;
    }
}
@media screen and (min-width: 980px) {
    .graphs-container {
        flex-direction: row;
    }
}
@media screen and (min-height: 800px) {
    .graphs-container {
        height: 80%;
    }
}
.chart-width {
    width: 30%;
}
.chart-width2 {
    width: 100%;
}
:deep(.loader) {
    margin: 25px 40%;
    display: flex;
    justify-content: end;
    align-items: center;
}
@media screen and (max-height: 700px) {
    :deep(.title) {
        max-width: 750px;
    }
}
</style>
